import { useEffect, useState } from 'react';
import { useAuth } from '@aimsos/auth';
import { ShellAuthProvider } from '@aimsos/auth';
import { WorkQueuesApp } from '@aimsos/work-queues';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Work Queues application inside the Core Shell.
 * Mirrors GovernanceEmbed: expose API base URLs via __AIMS_SHELL__ so the
 * lazy clients in work-queues read them on first request.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where the Auth0 SDK itself has no session.
 */
const WorkQueuesEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [wqI18nReady, setWqI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/work-queues/i18n/register')
      .then(({ registerWorkQueuesI18n }) => {
        registerWorkQueuesI18n(registerNamespace);
        return i18n.loadNamespaces('work-queues-app');
      })
      .then(() => {
        if (!cancelled) setWqI18nReady(true);
      })
      .catch((err) => console.error('[WorkQueuesEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlGS: import.meta.env.VITE_API_BASE_URL_GS,
      apiBaseUrlProject: import.meta.env.VITE_API_BASE_URL_PROJECT,
      workQueuesProjectApi: import.meta.env.VITE_WQ_PROJECT_API,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !wqI18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <WorkQueuesApp />
    </ShellAuthProvider>
  );
};

export default WorkQueuesEmbed;
